import { ViewEncapsulation, Component, ElementRef, OnInit, ViewChild, Inject } from '@angular/core';
import { FormGroup, FormControl, FormBuilder } from '@angular/forms';

import { AutoCxService } from '../../services/auto-cx-service';

import * as Blockly from 'blockly';

import { ToastService } from 'src/app/shared/services/toast.service';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { BaseWrapperDirective } from 'src/app/shared/classes/base-wrapper';
import { toolboxData } from '@75f/blockly-lib';
import { Helper } from 'src/app/shared/helpers/helper';
import { BlockStatistics } from '../../models/BlockStatistics.model';
import { AutoCommissioningBlock, AutoCommissioningNode } from '../../models/AutoCommissioning.model';
import { BlockType } from '../../enums/BlockType.enum';
import { RuleSet } from '../../models/site-checkout/RuleSet.model';
import { FDDService } from '../../services/fdd.service';
import { DialogService } from 'src/app/shared/services/dialog.service';

@Component({
    selector: 'app-auto-cx-blockly',
    templateUrl: 'auto-cx-blockly.component.html',
    styleUrls: ['auto-cx-blockly.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class AutoCxBlockly extends BaseWrapperDirective implements OnInit {
    block: AutoCommissioningBlock;
    node?: AutoCommissioningNode;
    ruleSet?: RuleSet;
    newBlock: boolean;
    editMode: boolean = true;

    blockManagementGroup: FormGroup;

    workspaceSvg: Blockly.WorkspaceSvg;
    blocklyWorkspaceInit: boolean = false;

    displayIcons = [
        {
            color: 'rgb(223, 124, 3)',
            icon: 'fas fa-fire'
        },
        {
            color: 'dodgerblue',
            icon: 'far fa-snowflake'
        },
        {
            color: 'rgb(108 168 191)',
            icon: 'fas fa-fan'
        },
        {
            color: 'rgb(25 205 72)',
            icon: 'fas fa-wind'
        },
        {
            color: 'rgb(223, 124, 3)',
            icon: 'fas fa-temperature-high'
        },
        {
            color: 'dodgerblue',
            icon: 'fas fa-temperature-low'
        },
        {
            color: 'rgb(25 197 205)',
            icon: 'far fa-clock'
        },
        {
            color: 'rgb(233 160 27)',
            icon: 'fas fa-stopwatch'
        },
        {
            color: 'rgb(160 160 160)',
            icon: 'fas fa-power-off'
        },
        {
            color: 'rgb(25 197 205)',
            icon: 'fas fa-server'
        },
        {
            color: 'rgb(233 160 27)',
            icon: 'fas fa-bolt'
        },
        {
            color: 'rgb(25 82 205)',
            icon: 'fas fa-sliders'
        },
        {
            color: '#ffc800',
            icon: 'far fa-lightbulb'
        },
        {
            color: 'rgb(221 88 108)',
            icon: 'fas fa-gears'
        }
    ];

    blockIconSetting: any;

    isValidBlocklyLogic: boolean = false;
    blockStatistics: BlockStatistics;
    blockErrors: any = [];

    // Toolbox element
    @ViewChild('toolbox') toolbox: ElementRef;

    constructor(
        private readonly formBuilder: FormBuilder,
        private readonly autoCxService: AutoCxService,
        private readonly fddService: FDDService,
        private readonly toastService: ToastService,
        @Inject(MAT_DIALOG_DATA) public data: any,
        public dialogRef: MatDialogRef<AutoCxBlockly>,
        private readonly dialogService: DialogService,
        private readonly helper: Helper,
    ) {
        super();
        this.block = data.block;
        this.node = data.node;
        this.newBlock = data.newBlock;
        this.editMode = data.editMode;
        if (this.node?.ruleSetId) {
            this.fddService.getRuleSetById(this.node.ruleSetId).subscribe((ruleSet: RuleSet) => {
                this.ruleSet = ruleSet;
            });
        }
    }

    ngOnInit() {
        this.buildForms();
        if (!this.newBlock && this.block?.presentationData) {
            this.setBlockIconSetting(this.block.presentationData);
        } else {
            this.blockIconSetting = {
                color: 'rgb(221 88 108)',
                icon: 'fas fa-gears'
            };
        }
        this.createBlocklyInstance(false, false, "start");
    }

    buildForms() {
        this.blockManagementGroup = this.formBuilder.group({
            opCode: new FormControl(),
            blockName: new FormControl(),
            blockDescription: new FormControl(),
            blockType: new FormControl(),
            blockData: new FormControl(),
            blockNumInputs: new FormControl(),
            blockNumOutputs: new FormControl(),
            blockIsPublished: new FormControl()
        });
    }

    createBlocklyInstance(rtl, horizontalLayout, position) {
        let workspaceOptions: any = {
            comments: true,
            collapse: true,
            disable: true,
            maxBlocks: Infinity,
            media: null,
            oneBasedIndex: true,
            scrollbars: true,
            trashcan: true,
            zoom: {
                controls: true,
                wheel: false,
                startScale: 1.0,
                maxScale: 4,
                minScale: 0.25,
                scaleSpeed: 1.1
            },
            toolbox: toolboxData,
            toolboxPosition: position,
            rtl: rtl,
            horizontalLayout: horizontalLayout,
            readOnly: !this.editMode
        };

        if (!this.workspaceSvg) {
            this.workspaceSvg = Blockly.inject("blocklyContainer", workspaceOptions);

            this.workspaceSvg.addChangeListener((_) => this.validateBlocklyLogic());

            setTimeout(() => {
                Blockly.svgResize(this.workspaceSvg);
            }, 10);
        }

        if (!this.newBlock) { // if this is an existing block's dialog, populate the form fields and Blockly workspace with xml
            if (this.block) {
                this.blockManagementGroup.controls["opCode"].setValue(this.block.opCode);
                this.blockManagementGroup.controls["blockName"].setValue(this.block.name);
                this.blockManagementGroup.controls["blockType"].setValue(this.block.blockType);
                this.blockManagementGroup.controls["blockDescription"].setValue(this.block.description);
                this.blockManagementGroup.controls["blockNumInputs"].setValue(this.block.numInputs);
                this.blockManagementGroup.controls["blockNumOutputs"].setValue(this.block.numOutputs);
                this.blockManagementGroup.controls["blockIsPublished"].setValue(this.block.isPublished);

                this.setBlockIconSetting(this.block.presentationData);

                // if xml data exists for this node, populate the Blockly workspace
                if (this.block.executionData.xml) {
                    let dom = Blockly.Xml.textToDom(this.block.executionData.xml);
                    Blockly.Xml.domToWorkspace(dom, Blockly.getMainWorkspace());
                }
            }
        }
    }

    validateBlocklyLogic() {
        try {
            this.blockStatistics = this.autoCxService.evaluateWorkspace(Blockly.getMainWorkspace());
            let errors = [];

            if (!this.blockStatistics.isFanLoopEnabled) {
                errors.push({
                    message: "Fan loop must be enabled for system-level tests",
                    type: "ERROR"
                })
            }
            if (this.blockStatistics.isTempOutOfBounds) {
                errors.push({
                    message: "Temperature Setpoints must be within 50 and 120 °F",
                    type: "ERROR"
                })
            }
            if (this.totalBlockRuntime == 0) {
                errors.push({
                    message: "Block cannot have zero runtime",
                    type: "ERROR"
                })
            }
            this.blockErrors = [...errors];
        }
        catch (error) {
            this.isValidBlocklyLogic = false;
            return;
        }
        this.isValidBlocklyLogic = true;
    }

    get totalBlockRuntime() {
        return this.blockStatistics.systemCommandDuration + this.blockStatistics.zoneCommandDuration + this.blockStatistics.totalDelay;
    }

    /**
     * A block can only be submitted iff:
     * 1) Any of the block parameters have been updated (name, description, meta-data)
     * 2) The Blockly logic has no errors
     */
    get isValid() {
        return !(this.blockManagementGroup.controls["blockName"].value === this.block?.name &&
            this.blockManagementGroup.controls["blockDescription"].value === this.block?.description &&
            this.block?.data === this.blockXml) &&
            this.isValidBlocklyLogic &&
            this.blockErrors.length == 0 &&
            !this.helper.isBlank(this.blockManagementGroup.controls["blockName"].value) &&
            !this.helper.isBlank(this.blockManagementGroup.controls["blockDescription"].value)
    }

    get blockXml() {
        let dom = Blockly.Xml.workspaceToDom(Blockly.getMainWorkspace());
        return Blockly.Xml.domToText(dom);
    }

    saveAsNewBlock() {
        const baseName = this.blockManagementGroup.controls["blockName"].value + " Copy";

        this.subs.sink = this.autoCxService.getBlocks().subscribe((existingBlocks: AutoCommissioningBlock[]) => {
            const uniqueName = this.resolveUniqueName(baseName, existingBlocks);

            const newBlock: AutoCommissioningBlock = {
                name: uniqueName,
                blockType: BlockType.CONTROL,
                executionData: { xml: this.blockXml },
                description: this.blockManagementGroup.controls["blockDescription"].value,
                presentationData: this.buildPresentationData(),
                isPublished: false,
                runtimeMinutes: this.totalBlockRuntime,
                numInputs: 1,
                numOutputs: 1,
            };

            this.subs.sink = this.autoCxService.createBlock(newBlock).subscribe(() => {
                this.autoCxService.loadBlockLibrary.next();
                this.toastService.success("Successfully created block!");
                this.close();
            }, (error) => {
                this.toastService.error({ message: "Failed to create block" });
                console.error(error);
            });
        }, (error) => {
            this.toastService.error({ message: "Failed to validate block name uniqueness" });
            console.error(error);
        });
    }

    updateBlock() {
        const xml_text = this.blockXml;
        const updateBlock: AutoCommissioningBlock = {
            opCode: this.blockManagementGroup.controls["opCode"].value,
            name: this.blockManagementGroup.controls["blockName"].value,
            blockType: this.blockManagementGroup.controls["blockType"].value,
            executionData: { xml: xml_text },
            description: this.blockManagementGroup.controls["blockDescription"].value,
            presentationData: this.buildPresentationData(),
            isPublished: this.blockManagementGroup.controls["blockIsPublished"].value,
            runtimeMinutes: this.totalBlockRuntime,
            numInputs: this.blockManagementGroup.controls["blockNumInputs"].value,
            numOutputs: this.blockManagementGroup.controls["blockNumOutputs"].value,
        }
        this.subs.sink = this.autoCxService.updateBlock(updateBlock).subscribe(() => {
            this.autoCxService.loadBlockLibrary.next();
            this.toastService.success("Successfully updated block!");
            this.close();
        }, (error) => {
            this.toastService.error({ message: "Failed to update block" });
            console.error(error);
        });
    }

    viewDiagnostics() {
        this.dialogService.openRuleSets(this.ruleSet);
    }

    /**
     * Finds a unique name for a copied block by appending (1), (2), etc to the base name until a unique name is found.
     * @param baseName The base name of the block.
     * @param existingBlocks The list of existing blocks to check against.
     * @returns A unique name for the copied block.
     */
    private resolveUniqueName(baseName: string, existingBlocks: AutoCommissioningBlock[]): string {
        const existingNames = new Set(existingBlocks.map(b => b.name));

        if (!existingNames.has(baseName)) {
            return baseName;
        }

        let suffix = 2;
        while (existingNames.has(`${baseName} (${suffix})`)) {
            suffix++;
        }
        return `${baseName} (${suffix})`;
    }

    private buildPresentationData() {
        return {
            icon: this.blockIconSetting.icon,
            color: this.blockIconSetting.color
        }
    }

    private setBlockIconSetting(presentationData: any) {
        this.blockIconSetting = {
            icon: presentationData.icon,
            color: presentationData.color
        }
    }

    /**
     * Evaluates whether or not "Save" action should be enabled for this block.
     */
    isSaveAllowed() {
        return this.editMode && !this.newBlock;
    }

    close() {
        this.dialogRef.close();
    }

    compareIcons = (o1: any, o2: any): boolean => {
        return o1 && o2 && o1.icon === o2.icon && o1.color === o2.color;
    }
}
